import React from "react";
import clsx from "clsx";
import CircularLoader from "app/components/CircularLoader";
import Actions from "app/components/FileUploadingView/Actions";
import ProfileAvatar from "app/components/ProfileAvatar";
import { ExpandableIcon } from "app/components/Icons";
import { FileCardOptionsTypeEnum } from "app/components/MediaUploader/types";
import type { TicketAttachmentFile } from "app/containers/Features/containers/Ticket/types";
import { dateFormatter } from "utils/helpers";

type AttachmentCardProps = {
  variant: "uploading" | "uploaded";
  fileName: string;
  avatar: any;
  userName: string;
  file?: TicketAttachmentFile;
  renameAttachmentLoading?: boolean;
  isRenaming?: boolean;
  renameValue?: string;
  onAction?: (
    type: FileCardOptionsTypeEnum,
    file: TicketAttachmentFile,
  ) => void | Promise<void>;
  onPreview?: (file: TicketAttachmentFile) => void;
  onRenameValueChange?: (value: string) => void;
  onRenameSubmit?: () => void;
  onRenameCancel?: () => void;
  renameInputRef?: React.RefObject<HTMLInputElement>;
  renderFileMedia?: (file: TicketAttachmentFile) => React.ReactNode;
};

const AttachmentCard: React.FC<AttachmentCardProps> = ({
  variant,
  fileName,
  avatar,
  userName,
  file,
  renameAttachmentLoading = false,
  isRenaming = false,
  renameValue = "",
  onAction,
  onPreview,
  onRenameValueChange,
  onRenameSubmit,
  onRenameCancel,
  renameInputRef,
  renderFileMedia,
}) => {
  if (variant === "uploading") {
    return (
      <div className="bg-white rounded-md !p-0 h-[200px] cursor-pointer relative group border border-gray-50">
        <div className="relative h-[150px] rounded-t overflow-hidden justify-center items-center flex">
          <CircularLoader size="xl" />
        </div>
        <div className="border-t border-gray-100 px-2.5 h-[50px] flex justify-between items-center gap-1.5">
          <div className="flex flex-col justify-start w-full truncate">
            <div className="flex inline-flex text-sm text-black font-normal">
              <span className="truncate">{fileName}</span>
            </div>
            <span className="text-xs text-secondary">Uploading...</span>
          </div>
          <ProfileAvatar
            avatar={avatar}
            name={userName}
            circleClassName="!size-[25px]"
            statusClassName="!size-[8px]"
          />
        </div>
      </div>
    );
  }

  if (!file || !renderFileMedia) return null;

  const previewBlocked = isRenaming || renameAttachmentLoading;

  return (
    <div
      className={clsx(
        "group relative h-[200px] w-full min-w-0 rounded-[6px] border border-gray-50 bg-white",
        renameAttachmentLoading ? "cursor-default opacity-90" : "cursor-pointer",
      )}
      onClick={() => !previewBlocked && onPreview?.(file)}
      onKeyDown={(e) => {
        if (previewBlocked) return;
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        onPreview?.(file);
      }}
      role="button"
      tabIndex={renameAttachmentLoading ? -1 : 0}
    >
      <div
        className={clsx(
          "absolute top-1 right-1 z-30",
          renameAttachmentLoading && "opacity-50",
        )}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <Actions
          actions={{
            onRename: () => onAction?.(FileCardOptionsTypeEnum.IS_EDIT, file),
            onCopy: () => onAction?.(FileCardOptionsTypeEnum.COPY_URL, file),
            onDownload: () => onAction?.(FileCardOptionsTypeEnum.DOWNLOAD, file),
            onDelete: () => onAction?.(FileCardOptionsTypeEnum.IS_REMOVE, file),
          }}
        />
      </div>
      <div className="relative flex h-[140px] items-center justify-center overflow-hidden rounded-t-[6px]">
        {renderFileMedia(file)}
        <div className="absolute inset-0 flex items-center justify-center bg-[#00000099] text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <div className="flex h-8 w-8 items-center justify-center">
            <ExpandableIcon size={24} color="#FFFFFF" />
          </div>
        </div>
      </div>
      <div className="flex min-h-[60px] items-center justify-between border-t border-gray-50 px-2 py-2">
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-[6px] pr-2">
          {isRenaming ? (
            <input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => onRenameValueChange?.(e.target.value)}
              onBlur={onRenameSubmit}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onRenameSubmit?.();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  onRenameCancel?.();
                }
              }}
              className="h-7 w-full rounded px-2 text-sm font-normal leading-[21px] tracking-[0] text-black focus:outline-none"
            />
          ) : (
            <div className="flex inline-flex text-sm font-normal leading-[21px] tracking-[0] text-black">
              <span className="truncate">{file.name}</span>
            </div>
          )}
          <p className="text-xs font-normal leading-[18px] tracking-[0] text-secondary">
            {dateFormatter(file.createdAt, "noYearDatetime")}
          </p>
        </div>
        <div className="flex items-center">
          <ProfileAvatar
            avatar={avatar}
            name={userName}
            circleClassName="!size-[25px]"
            statusClassName="!size-[8px]"
          />
        </div>
      </div>
    </div>
  );
};

export default AttachmentCard;
